import { CloudUpload } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    CircularProgress,
    FormControlLabel,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { resourcesApi } from '../services/api';

const AddResource: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [imagePreview, setImagePreview] = useState<string | null>(null);

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        category: '',
        quantity: 1,
        imageUrl: '',
        isActive: true,
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, checked, type } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : type === 'number' ? parseInt(value) || 0 : value,
        });
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Please select an image file');
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            setError('Image size must be less than 5MB');
            return;
        }

        try {
            setUploadingImage(true);
            setError(null);

            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result as string);
            };
            reader.readAsDataURL(file);

            // Upload to Azure Blob Storage
            const result = await resourcesApi.uploadImage(file);
            setFormData({
                ...formData,
                imageUrl: result.url,
            });
        } catch (err) {
            console.error('Error uploading image:', err);
            setError('Failed to upload image. Please try again.');
            setImagePreview(null);
        } finally {
            setUploadingImage(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validation
        if (!formData.name.trim()) {
            setError('Resource name is required');
            return;
        }
        if (formData.quantity <= 0) {
            setError('Quantity must be greater than 0');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            await resourcesApi.create(formData);
            setSuccess(true);

            // Redirect to resources page after a short delay
            setTimeout(() => {
                navigate('/resources');
            }, 2000);
        } catch (err: any) {
            console.error('Error creating resource:', err);
            setError(err.response?.data?.error || 'Failed to create resource. Please try again.');
            setLoading(false);
        }
    };

    if (success) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                <Alert severity="success">
                    Resource created successfully! Redirecting...
                </Alert>
            </Box>
        );
    }

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">Add New Resource</Typography>
                <Button variant="outlined" onClick={() => navigate('/resources')}>
                    Cancel
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Card>
                <CardContent>
                    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <TextField
                            label="Resource Name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            fullWidth
                            helperText="e.g., Sundance Ski Pass"
                        />

                        <TextField
                            label="Description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            multiline
                            rows={4}
                            fullWidth
                            helperText="Provide a detailed description of the resource"
                        />

                        <TextField
                            label="Category"
                            name="category"
                            value={formData.category}
                            onChange={handleInputChange}
                            fullWidth
                            helperText="e.g., Ski Passes, Conference Tickets, etc."
                        />

                        <TextField
                            label="Quantity"
                            name="quantity"
                            type="number"
                            value={formData.quantity}
                            onChange={handleInputChange}
                            required
                            fullWidth
                            inputProps={{ min: 1 }}
                            helperText="Number of available units"
                        />

                        <Box>
                            <Typography variant="subtitle1" gutterBottom>
                                Resource Image
                            </Typography>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={uploadingImage ? <CircularProgress size={20} /> : <CloudUpload />}
                                disabled={uploadingImage}
                                fullWidth
                                sx={{ mb: 2 }}
                            >
                                {uploadingImage ? 'Uploading...' : 'Upload Image'}
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    disabled={uploadingImage}
                                />
                            </Button>

                            {imagePreview && (
                                <Card sx={{ maxWidth: 300 }}>
                                    <CardMedia
                                        component="img"
                                        height="140"
                                        image={imagePreview}
                                        alt="Preview"
                                        sx={{ objectFit: 'contain', bgcolor: '#f5f5f5', p: 1 }}
                                    />
                                </Card>
                            )}

                            {formData.imageUrl && (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                                    Image uploaded successfully
                                </Typography>
                            )}
                        </Box>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isActive}
                                    onChange={handleInputChange}
                                    name="isActive"
                                />
                            }
                            label="Active (Available for reservation)"
                        />

                        <Box display="flex" gap={2} justifyContent="flex-end">
                            <Button
                                variant="outlined"
                                onClick={() => navigate('/resources')}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={loading || uploadingImage}
                                startIcon={loading && <CircularProgress size={20} />}
                            >
                                {loading ? 'Creating...' : 'Create Resource'}
                            </Button>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default AddResource;
